import { Form, Formik } from 'formik'
import { observer } from 'mobx-react-lite'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CampaignContext } from '../../../../contexts/CampaignContext'
import { useSidebarCollapse } from '../../../../contexts/SidebarCollapseContext'
import CampaignReport from '../../../../models/CampaignReport'
import axiosService from '../../../../utils/AxiosService'
import { toastError, toastInfo, toastSuccess } from '../../../FormComponents/Toasts'
import AudienceMetrics from './ReportParts/AudienceMetrics'
import CampaignOverview from './ReportParts/CampaignOverview'
import ContactUs from './ReportParts/ContactUs'
import General from './ReportParts/General'
import ImportantMetrics from './ReportParts/ImportantMetrics'
import InfluencerOverview from './ReportParts/InfluencerOverview'
import LearningRecommendations from './ReportParts/LearningRecommendations'
import SurveryLink from './ReportParts/SurveryLink'
import TimelineOverview from './ReportParts/TimelineOverview'
import { FieldsLockedContext, useReadOnly } from '../../../../contexts/ReadOnlyContext'

export default observer(function Report() {
  const { id } = useParams()
  const readOnly = useReadOnly()
  const { campaignState, campaignReport, campaign, selectedScenario } = useContext(CampaignContext)
  const { isCollapsed } = useSidebarCollapse()
  const [startDate, setStartDate] = useState()
  const [endDate, setEndDate] = useState()
  const [missingContentDates, setMissingContentDates] = useState(false)

  const getDates = () => {
    if(!selectedScenario || !campaignReport.id || campaignReport.start_date) return
    axiosService.get(`/api/project_manager/campaigns/${id}/campaign_scenarios/${selectedScenario.id}/date_range`)
    .then(({ min_date, max_date }) => {
      setStartDate(min_date)
      setEndDate(max_date)
      const missing = !min_date || !max_date
      setMissingContentDates(missing)
      if (missing) toastInfo('Add dates to the content pieces to pre-fill the report timeframe.')
    })
    .catch(({ data }) => toastError(data?.error || 'Could not load the content dates for this report'))
  }

  useEffect(() => {
    getDates()
  }, [selectedScenario, campaignReport.id])

  const submit = (data) => {
    axiosService.patch(`/api/project_manager/campaigns/${id}/campaign_report`, { campaign_report: data })
    .then(updatedReport => {
      campaignState.campaignReport = new CampaignReport(updatedReport)
      toastSuccess('Report Updated')
    })
    .catch(({ data: { error }}) => toastError(error))
  }

  if(!selectedScenario) return null

  const leftPosition = isCollapsed ? 128 : 223
  const width = isCollapsed ? 'calc(100vw - 128px)' : 'calc(100vw - 223px)'

  return (
    <div
      className={`fixed top-[52px] h-full flex justify-center overflow-y-auto dark:bg-rb-dark-mode-background transition-all duration-300`}
      style={{
        left: `${leftPosition}px`,
        height: 'calc(100vh - 52px)',
        width: width
      }}
    >
      <div className="w-[800px] mt-10">

        <p className="font-bold text-2xl mb-4 dark:text-white">Campaign Reporting & Performance Monitoring</p>

        <Formik
            enableReinitialize
            initialValues={{
              campaign_name: campaignReport.campaign_name || campaign.name,
              general_visible: campaignReport.general_visible,
              campaign_overview_visible: campaignReport.campaign_overview_visible,
              default_campaign_overview_sections_attributes: campaignReport.default_campaign_overview_sections,
              custom_campaign_overview_sections_attributes: campaignReport.custom_campaign_overview_sections,
              timeframe_visible: campaignReport.timeframe_visible,
              survey_link: campaignReport.survey_link,
              survey_link_title: campaignReport.survey_link_title,
              survey_link_visible: campaignReport.survey_link_visible,
              audience_metrics_visible: campaignReport.audience_metrics_visible,
              start_date: campaignReport.start_date ? new Date(campaignReport.start_date) : startDate ? new Date(startDate) : new Date(),
              end_date: campaignReport.end_date ? new Date(campaignReport.end_date) : endDate ? new Date(endDate) : new Date(),
              learning_visible: campaignReport.learning_visible,
              timeline_visible: campaignReport.timeline_visible,
              learning_sections_attributes: campaignReport.learning_sections,
              important_metrics_visible: campaignReport.important_metrics_visible,
              influencer_amount: campaignReport.influencer_amount || campaign.influencer_target,
              influencer_amount_reason: campaignReport.influencer_amount_reason,
              upload_amount: campaignReport.upload_amount || campaign.action_count_target,
              upload_amount_reason: campaignReport.upload_amount_reason,
              gross_reach: campaignReport.gross_reach || campaign.gross_reach_target,
              gross_reach_reason: campaignReport.gross_reach_reason,
              net_reach: campaignReport.net_reach || campaign.net_reach_target,
              net_reach_reason: campaignReport.net_reach_reason,
              impression: campaignReport.impression || campaign.impressions_target,
              impression_reason: campaignReport.impression_reason,
              influencer_budget: campaignReport.influencer_budget || campaign.influencer_budget,
              influencer_budget_reason: campaignReport.influencer_budget_reason,
              social_ads_budget: campaignReport.social_ads_budget || campaign.social_budget,
              social_ads_budget_reason: campaignReport.social_ads_budget_reason,
              organic_impressions: campaignReport.organic_impressions,
              organic_reach: campaignReport.organic_reach,
              organic_engagement_rate: campaignReport.organic_engagement_rate,
              organic_top_performing_post_id: campaignReport.organic_top_performing_post_id,
              organic_comment: campaignReport.organic_comment,
              paid_impressions: campaignReport.paid_impressions,
              paid_reach: campaignReport.paid_reach,
              conversions: campaignReport.conversions,
              roi_visible: campaignReport.roi_visible,
              paid_roi_visible: campaignReport.paid_roi_visible,
              conversion_rate_visible: campaignReport.conversion_rate_visible,
              audience_gender_visible: campaignReport.audience_gender_visible,
              audience_age_visible: campaignReport.audience_age_visible,
              audience_countries_visible: campaignReport.audience_countries_visible,
              audience_cities_visible: campaignReport.audience_cities_visible,
              paid_revenue_generated: campaignReport.paid_revenue_generated,
              paid_clicks: campaignReport.paid_clicks,
              paid_return_of_investment_post_id: campaignReport.paid_return_of_investment_post_id,
              paid_spend_overview: campaignReport.paid_spend_overview,
              paid_comment: campaignReport.paid_comment,
              revenue_generated: campaignReport.revenue_generated,
              total_spend: campaignReport.total_spend,
              influencer_overview_visible: campaignReport.influencer_overview_visible,
              contact_us_visible: campaignReport.contact_us_visible,
              report_influencer_performances_attributes: campaignReport.report_influencer_performances
            }}
            onSubmit={submit}
          >
          {({ handleBlur, handleChange, values, setFieldValue }) => (
            <FieldsLockedContext.Provider value={readOnly}>
            <Form className="w-[800px]">
              <General values={values} setFieldValue={setFieldValue} handleChange={handleChange} handleBlur={handleBlur} campaign={campaign} />
              <CampaignOverview values={values} setFieldValue={setFieldValue} handleChange={handleChange} handleBlur={handleBlur} campaign={campaign} missingContentDates={missingContentDates} />
              <ImportantMetrics values={values} setFieldValue={setFieldValue} handleChange={handleChange} handleBlur={handleBlur} />
              <InfluencerOverview values={values} setFieldValue={setFieldValue} handleChange={handleChange} handleBlur={handleBlur} />
              <AudienceMetrics values={values} setFieldValue={setFieldValue} />
              <TimelineOverview values={values} setFieldValue={setFieldValue} selectedScenario={selectedScenario} />
              <LearningRecommendations values={values} setFieldValue={setFieldValue} handleChange={handleChange} handleBlur={handleBlur} />
              <SurveryLink values={values} setFieldValue={setFieldValue} handleChange={handleChange} handleBlur={handleBlur} />
              <ContactUs values={values} setFieldValue={setFieldValue} campaign={campaign} />
              {readOnly ? <div className="pb-8" /> : (
                <div className="flex justify-between mt-8 pb-8">
                  <button className="font-bold text-sm hover:bg-gray-200 dark:hover:bg-rb-dark-mode-lighter text-rb-black dark:text-white p-2 px-3 rounded-md" type='button'>Reset</button>
                  <div className="flex gap-4">
                    {/* <button className="font-bold text-sm hover:bg-gray-200 dark:hover:bg-rb-dark-mode-lighter text-rb-black dark:text-white p-2 px-3 rounded-md" type='button'>Preview</button> */}
                    <button className="font-bold text-sm bg-rb-green dark:bg-rb-green text-white dark:text-white p-2 px-3 rounded-md" type='submit'>Submit</button>
                  </div>
                </div>
              )}
            </Form>
            </FieldsLockedContext.Provider>
          )}
        </Formik>
      </div>
    </div>
  )
})